import React, { useState } from 'react';
import Table from '../../components/common/Table';
import Modal from '../common/Modal';
import { BatchFileLogDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { SearchCheck, FileText } from 'lucide-react'; 
import { useBatchFileLogApi } from '../../hooks/useBatchFileLogApi';
import { useFileApi } from '../../hooks/useFileApi';
import { FileViewer } from '../../components/common/FileViewer'; 

interface BatchFileLogTableProps {
  data: BatchFileLogDTO[];
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

// Add this new type for tabs
type TabType = 'formatted' | 'raw';

const BatchFileLogTable: React.FC<BatchFileLogTableProps> = ({
  data,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  const [statusModal, setStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<{
    fileToken: string;
    status: string;
  } | null>(null);
  // Add state for active tab
  const [activeTab, setActiveTab] = useState<TabType>('formatted');
  
  const { checkBatchFile, isLoading: isCheckingStatus } = useBatchFileLogApi();

  const { getFileUrl } = useFileApi();
  const [selectedFile, setSelectedFile] = useState<{ url: string; name: string } | null>(null);

  const handleViewMandate = async (record: any) => {
    try {
      if (!record.mandateFilePath) return;
      
      const response = await getFileUrl('mandates', record.mandateFilePath);
      if (response.success && response.data) {
        setSelectedFile({
          url: response.data,
          name: record.mandateFilePath.split('/').pop() || 'mandate.pdf'
        });
      }
    } catch (error) {
      console.error('Error getting mandate file:', error);
    }
  };

  const handleQueryStatus = async (batchFileLog: BatchFileLogDTO) => {
    try {
      const response = await checkBatchFile(batchFileLog.fileToken ?? '');
      if (response.success && response.data) {
        setSelectedStatus({
          fileToken: batchFileLog.fileToken ?? '',
          status: response.data
        });
        setStatusModal(true);
      }
    } catch (error) {
      console.error('Error checking batch status:', error);
    }
  };

  const formatStatusContent = (status: string) => {
    return (
      <div className="space-y-4">
        {/* Tab buttons */}
        <div className="flex space-x-2 border-b">
          <button
            className={`px-4 py-2 ${
              activeTab === 'formatted'
                ? 'border-b-2 border-primary text-primary font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('formatted')}
          >
            Formatted
          </button>
          <button
            className={`px-4 py-2 ${
              activeTab === 'raw'
                ? 'border-b-2 border-primary text-primary font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('raw')}
          >
            Raw
          </button>
        </div>

        {/* Content */}
        {activeTab === 'formatted' ? (
          <div className="space-y-4">
            {status.split('\r\n').filter(line => line.trim()).map((line, index) => {
              if (line.startsWith('###BEGIN')) {
                const [, id, status, time, amount, date] = line.split('\t');
                return (
                  <div key={index} className="bg-blue-50 p-4 rounded">
                    <h4 className="font-medium text-blue-900">Batch Details</h4>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                      <div>ID:</div><div className="font-mono">{id}</div>
                      <div>Status:</div>
                      <div className={`font-medium ${
                        status === 'SUCCESSFUL' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {status}
                      </div>
                      <div>Time:</div><div>{time}</div>
                      <div>Amount:</div><div>{amount}</div>
                      <div>Date:</div><div>{date}</div>
                    </div>
                  </div>
                );
              }
              
              if (line.startsWith('###ERROR:')) {
                return (
                  <div key={index} className="bg-red-50 p-4 rounded">
                    <h4 className="font-medium text-red-900">Error Details</h4>
                    <p className="text-sm text-red-700 mt-1">
                      {line.replace('###ERROR:\t', '')}
                    </p>
                  </div>
                );
              }
              
              if (line.startsWith('###END')) {
                return (
                  <div key={index} className="text-sm text-gray-500 italic">
                    Process completed at {line.split('\t')[1]}
                  </div>
                );
              }
              
              return null;
            })}
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono overflow-x-auto">
              {status}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Table
        columns={[
          {
            label: 'File Token',
            key: 'fileToken',
          },
          {
            label: 'Added',
            key: 'createdUtc',
            format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
          },
        ]}
        data={data}
        actions={{
          extra: [
            {
              label: 'Query Status',
              icon: <SearchCheck />,
              onClick: handleQueryStatus,
              visible: (row) => Boolean(row.fileToken), 
            },
            {
              label: 'View Mandate',
              icon: <FileText />,
              onClick: handleViewMandate,
              visible: (row) => Boolean(row.mandateFilePath),
            },
          ],
        }}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
      />

      {selectedFile && (
        <FileViewer
          fileName={selectedFile.name}
          sasUrl={selectedFile.url}
          onClose={() => setSelectedFile(null)}
        />
      )}

      <Modal
        isOpen={statusModal}
        onClose={() => {
          setStatusModal(false);
          setSelectedStatus(null);
        }}
        title={`Batch File Status - ${selectedStatus?.fileToken}`}
      >
        <div className="min-h-[200px]">
          {isCheckingStatus ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : selectedStatus?.status ? (
            formatStatusContent(selectedStatus.status)
          ) : (
            <div className="text-center text-gray-500">No status information available</div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default BatchFileLogTable;
